import clsx from 'clsx';
import { useEffect, useMemo } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { KTSVG } from 'src/_metronic/helpers/components/KTSVG';
import useConfirmationModal from 'src/components/modal/useConfirmationModal';
import Divider from 'src/components/utils/Divider';
import { useGetContactInfoTypesQuery } from 'src/reducers/app/api';

const Aggregators = ({ control, register, watch, errors, trigger, setValue }) => {
	const [confirmAction, confirmationModal] = useConfirmationModal();
	const { data: contactInfoTypes = [] } = useGetContactInfoTypesQuery();

	const rsvProviderOptions = useMemo(
		() =>
			contactInfoTypes
				.filter((item) => item.type === 'rsv_provider')
				.map((item) => ({
					value: item._id,
					label: item.title,
				})),
		[contactInfoTypes]
	);

	const {
		fields: aggregatorFields,
		append: appendAggregator,
		remove: removeAggregator,
	} = useFieldArray({
		control,
		name: 'contact_info.rsv_providers',
	});

	const selectedAggregators = watch('contact_info.rsv_providers') ?? [];
	const useRsvProviders = watch('online_booking_settings.use_rsv_providers');

	const aggregatorSelectOptions = useMemo(() => {
		const options = [...rsvProviderOptions];

		selectedAggregators.forEach((item) => {
			if (item?.type && !options.some((option) => option.value === item.type)) {
				options.push({ value: item.type, label: item.type });
			}
		});

		return options;
	}, [rsvProviderOptions, selectedAggregators]);

	const availableAggregatorOptions = useMemo(() => {
		const selectedTypes = selectedAggregators.map((item) => item?.type).filter(Boolean);
		return rsvProviderOptions.filter((option) => !selectedTypes.includes(option.value));
	}, [rsvProviderOptions, selectedAggregators]);

	useEffect(() => {
		if (!useRsvProviders && selectedAggregators.length > 0) {
			setValue('contact_info.rsv_providers', []);
			trigger('contact_info.rsv_providers');
		}
	}, [useRsvProviders, selectedAggregators.length, setValue, trigger]);

	const handleAppendAggregator = () => {
		const nextOption = availableAggregatorOptions[0];
		if (!nextOption) {
			return;
		}
		appendAggregator({ type: nextOption.value, val: '' });
		trigger('contact_info.rsv_providers');
	};

	const handleRemoveAggregator = (index) => {
		removeAggregator(index);
		trigger('contact_info.rsv_providers');
	};

	return (
		<>
			<div className="row pb-5">
				<div className="col-12">
					<div className="mb-0 fs-2 fw-bold">
						<FormattedMessage id="AGGREGATORS" />
					</div>
				</div>
			</div>

			<Controller
				name="online_booking_settings.use_rsv_providers"
				control={control}
				render={({ field: { value, onChange, ref, name } }) => (
					<div className="row">
						<div className="col-12">
							<div className="form-check form-switch form-check-custom form-check-solid">
								<input
									ref={ref}
									name={name}
									className="form-check-input"
									type="checkbox"
									id="aggregators-providers"
									aria-labelledby="aggregators-providers"
									onChange={(event) => onChange(event.target.checked)}
									checked={!!value}
								/>
								<label className="form-check-label secondary-text fs-6 fw-bold" htmlFor="aggregators-providers">
									<FormattedMessage id="LOCATION.SETTINGS.USE_AGGREGATORS" />
								</label>
							</div>
							<p className="mb-0 secondary-text fs-7 pt-2">
								<FormattedMessage id="LOCATION.SETTINGS.USE_AGGREGATORS_DESC" />
							</p>
						</div>
					</div>
				)}
			/>

			{useRsvProviders ? (
				<>
					{aggregatorFields.map((field, index) => (
						<div className="row pt-5 g-5" key={field.id}>
							<div className="col-12 col-sm-3">
								<div className="select-container">
									<select
										className="fs-6 fw-bold text-capitalize"
										aria-label="Select aggregator option"
										{...register(`contact_info.rsv_providers[${index}].type`)}
										defaultValue={field.type ?? aggregatorSelectOptions[0]?.value}
									>
										{aggregatorSelectOptions.map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</select>
									<i className="fa-solid fa-chevron-down arrow-icon"></i>
								</div>
							</div>
							<div className="col-12 col-sm-7">
								<div className="input-group">
									<input
										type="text"
										name={`aggregators[${index}].val`}
										{...register(`contact_info.rsv_providers[${index}].val`)}
										className={clsx('form-control form-control-solid p-4', {
											'is-invalid': errors?.contact_info?.rsv_providers && errors?.contact_info?.rsv_providers[index]?.val,
										})}
									/>
									{errors?.contact_info?.rsv_providers && errors?.contact_info?.rsv_providers[index] ? (
										<div className="fv-plugins-message-container">
											<div className="fv-help-block">
												<span role="alert">{errors?.contact_info?.rsv_providers[index].val.message}</span>
											</div>
										</div>
									) : null}
								</div>
							</div>

							<div className="col-12 col-sm-2 d-flex align-items-start">
								<button
									type="button"
									onClick={() => confirmAction(() => handleRemoveAggregator(index))}
									className="btn btn-light-danger btn-color-danger w-100 d-flex justify-content-center align-items-center p-4"
								>
									<KTSVG className="svg-icon-1" path="/media/icons/duotune/general/gen027.svg" />
									<div className="fs-7">
										<FormattedMessage id="GENERAL.DELETE" />
									</div>
								</button>
							</div>
						</div>
					))}

					<div className="row pt-5">
						<div className="col-12 col-sm-4">
							<button
								type="button"
								onClick={handleAppendAggregator}
								className="generic-button fs-6 w-100 fw-bold"
								disabled={availableAggregatorOptions.length === 0}
							>
								<FormattedMessage id="STORE.MODAL.DETAILS.ADD_AGGREGATOR" />
							</button>
							{availableAggregatorOptions.length === 0 && aggregatorFields.length !== 0 ? (
								<p className="mb-0 mt-2 secondary-text fs-7">
									<FormattedMessage id="STORE.MODAL.DETAILS.NO_MORE_AGGREGATORS" />
								</p>
							) : null}
						</div>
					</div>
				</>
			) : null}

			{confirmationModal}
			<Divider />
		</>
	);
};

export default Aggregators;
